import { INeuroContactInfo } from 'neuro-data-structures';
import { fetchWithOptions } from '../../utility/fetch';
import { getJWTData, parseJWTFromCookie } from '../../utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from '../../utility/logger';
import {
  convertPatientDataToUI,
  convertUIExitusDataToApiPatchFormat,
  convertUIPatientDataToApiPatchFormat,
  convertUIPatientDataToApiPostFormat,
  IDelegate,
  IPatientDataForm,
} from './util';

/**
 * Fetch patient data
 * @returns {IPatient|void} IPatient | null
 */
export const fetchPatientData = async (): Promise<IPatientUI | null> => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/data/${orgid}/${patientid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(res => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .then((data: IPatientAPI) => {
      return convertPatientDataToUI(data);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET patient'));
      return null;
    });
};

/**
 * Update patient data
 * @param {IPatient} data - Patient data
 * @returns {Promise<boolean|null>} if patch was successful
 */
export const patchPatient = async (
  data: Partial<IPatientUI> & { contactNotAllowed?: boolean | null },
): Promise<boolean | null> => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/data/${orgid}/${patientid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify(convertUIPatientDataToApiPatchFormat(data)) },
  )
    .then((res: Response) => {
      if (res.status === 200) return true;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'PATCH patient'));
      return null;
    });
};

/**
 * Update patient exitus data
 * @param {TExitus} data - Exitus data
 * @returns {Promise<boolean|null>} if patch was successful
 */
export const patchPatientExitus = async (data: TExitusUI): Promise<boolean | null> => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/exitus/${orgid}/${patientid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify(convertUIExitusDataToApiPatchFormat(data)) },
  )
    .then((res: Response) => {
      if (res.status === 200) return true;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'PATCH patient exitus'));
      return false;
    });
};

/**
 * Create new patient
 * @param {Partial<IPatientUI>} data - Most likely incomplete patient data
 * @returns {Promise<Response|null>} if creation was successful
 */
export const createPatient = async (data: IPatientDataForm): Promise<Response | null> => {
  const { orgid } = getJWTData() || { orgid: null };
  if (!orgid) return null;

  return fetchWithOptions(
    `/api/patient/v2/patient/${orgid}`,
    { neurojwt: parseJWTFromCookie(), jmr: 'true' },
    { method: 'POST', body: JSON.stringify(convertUIPatientDataToApiPostFormat(data)) },
  )
    .then((res: Response) => {
      if (res.status === 200) return res;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'POST patient'));
      return null;
    });
};

/**
 * Fetch patient grants
 * @returns {TNeuroGrant[] | null} List of grants or null
 */
export const fetchPatientGrants = async (): Promise<TNeuroGrant[] | null> => {
  const { orgid, patientid } = getJWTData() || {};
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/grant/${orgid}/${patientid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(res => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .then((data: TNeuroGrant[]) => {
      return data;
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET grants'));
      return null;
    });
};

/**
 * Create new grant
 * @param {TNeuroGrant['granteeOrg']} granteeOrg - Organization to which the grant is given
 * @param {TNeuroGrant['flags']} flags - Which flags are given
 * @returns {TNeuroGrant | null} Full grant data or null
 */
export const createGrant = async (
  granteeOrg: TNeuroGrant['granteeOrg'],
  allow_write: TNeuroGrant['allow_write'],
  allow_read: TNeuroGrant['allow_read'],
): Promise<TNeuroGrant | null> => {
  const { orgid, patientid } = getJWTData() || {};
  if (!orgid || !patientid) return null;

  const body = {
    granteeGroup: 'FI',
    granteeOrg,
    allow_write,
    allow_read,
  };

  return fetchWithOptions(
    `/api/patient/v2/grant/${orgid}/${patientid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(body) },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .then((res: TNeuroGrant) => {
      return res;
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'POST grant'));
      return null;
    });
};

/**
 * Update existing grant
 * @param {TNeuroGrant['id']} id - Grant id
 * @param {{ granteeGroup?: TNeuroGrant['granteeGroup']; granteeOrg?: TNeuroGrant['granteeOrg']; flags?: TNeuroGrant['flags']; }} data - Grant data
 * @returns {TNeuroGrant | null} Updated grant data or null
 */
export const updateGrant = async (
  id: TNeuroGrant['id'],
  data: {
    granteeGroup?: TNeuroGrant['granteeGroup'];
    granteeOrg?: TNeuroGrant['granteeOrg'];
    allow_write?: TNeuroGrant['allow_write'];
    allow_read?: TNeuroGrant['allow_read'];
  },
): Promise<TNeuroGrant | null> => {
  const { orgid, patientid } = getJWTData() || {};
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/grant/${orgid}/${patientid}/${id}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PUT', body: JSON.stringify(data) },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .then((res: TNeuroGrant) => {
      return res;
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'PUT grant'));
      return null;
    });
};

/**
 * Delete existing grant
 * @param {TNeuroGrant['id']} id - Grant id
 * @returns {TNeuroGrant['id'] | null} Grant id or null
 */
export const deleteGrant = async (id: TNeuroGrant['id']): Promise<TNeuroGrant['id'] | null> => {
  const { orgid, patientid } = getJWTData() || {};
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/grant/${orgid}/${patientid}/${id}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'DELETE' },
  )
    .then((res: Response) => {
      if (res.status === 200) return id;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'DELETE grant'));
      return null;
    });
};

/**
 * Fetch patient delegates
 *
 */
export const fetchPatientDelegates = async (): Promise<IDelegate[]> => {
  return fetchWithOptions(`/api/delegates/v1`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(res => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET delegates'));
      return null;
    });
};

/**
 * Fetch patient contact info
 *
 */
export const fetchPatientContactInfo = async (): Promise<{
  contactNotAllowed: boolean;
  contacts: INeuroContactInfo[];
}> => {
  return fetchWithOptions(`/api/patient/v2/contacts`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(res => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET patient contact info'));
      return null;
    });
};

/**
 * Post a new patient contact info
 * @param {INeuroUIContactInfo} data - Contact info data
 * @returns {Promise<INeuroContactInfo|null>} Created contact info
 */
export const postPatientContactInfo = async (data: INeuroUIContactInfo): Promise<INeuroContactInfo | null> => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v2/contacts`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(data) },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .then((res: INeuroContactInfo) => {
      return res;
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'POST patient contact info'));
      return null;
    });
};

/**
 * Delete existing Patient contact info
 * @param {INeuroContactInfo['id']} id - Contact info id
 * @returns {INeuroContactInfo['id'] | null} Contact info id or null
 */
export const deletePatientContactInfo = async (
  id: INeuroContactInfo['id'],
): Promise<INeuroContactInfo['id'] | null> => {
  const { orgid, patientid } = getJWTData() || {};
  if (!orgid || !patientid) return null;

  return fetchWithOptions(`/api/patient/v2/contacts/${id}`, { neurojwt: parseJWTFromCookie() }, { method: 'DELETE' })
    .then((res: Response) => {
      if (res.status === 200) return id;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'DELETE patient contact info'));
      return null;
    });
};

/**
 * Fetch patient informants
 *
 */
export const fetchPatientInformants = async (): Promise<Array<IInformant> | null> => {
  return fetchWithOptions(`/api/patient/v1/informants`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(res => {
      if (res.status === 200) return res.json() as unknown as Array<IInformant>;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET patient informants'));
      return null;
    });
};

/**
 * Post a new patient informant
 * @param {Omit<IInformant, 'id'>} data - informant data
 * @returns {Promise<IInformant|string|null>} Created informant or string 'Informant already exists' if the response status was 409.
 */
export const postPatientInformant = async (data: Omit<IInformant, 'id'>): Promise<IInformant | null | string> => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v1/informants`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify({ ...data, kind: null, source: 'neuro-ui' }) },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json() as unknown as IInformant;
      if (res.status === 409) return 'Informant already exists';
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'POST patient informant'));
      return null;
    });
};

/**
 * Delete existing patient informant
 * @param {string} id - informant id
 * @returns {string | null} informant id or null
 */
export const deletePatientInformant = async (id: string): Promise<string | null> => {
  const { orgid, patientid } = getJWTData() || {};
  if (!orgid || !patientid) return null;

  return fetchWithOptions(`/api/patient/v1/informants/${id}`, { neurojwt: parseJWTFromCookie() }, { method: 'DELETE' })
    .then((res: Response) => {
      if (res.status === 200) return id;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'DELETE patient informant'));
      return null;
    });
};

/**
 * Update patient informant data
 * @param {Parital<IInformant> & Pick<IInformant, 'id'>} data - informant data to be updated
 * @returns {Promise<boolean|null>} true if patch was successful
 */
export const patchPatientInformant = async (
  data: Partial<IInformant> & Pick<IInformant, 'id'>,
): Promise<boolean | null> => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;

  return fetchWithOptions(
    `/api/patient/v1/informants/${data.id}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify(data) },
  )
    .then((res: Response) => {
      if (res.status === 200) return true;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, `PATCH informant with id ${data.id}`));
      return false;
    });
};

/**
 * Link patient to an existing informant
 * @param {string} informantId - identifier of the informant
 * @returns {Promise<boolean|null>} true if the linking was successful
 */
export const linkPatientToInformant = async (informantId: string) => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;
  return fetchWithOptions(
    `/api/patient/v1/informants/link/${informantId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH' },
  )
    .then((res: Response) => {
      if (res.status === 200) return true;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(
        Loggable.BugActionable,
        e,
        new ActionableErrorContext(null, `PATCH linking patient "${patientid}" to informant "${informantId}"`),
      );
      return false;
    });
};

/**
 * Unlink patient to an existing informant
 * @param {string} informantId - identifier of the informant
 * @returns {Promise<boolean|null>} true if the unlinking was successful
 */
export const unlinkPatientFromInformant = async (informantId: string) => {
  const { orgid, patientid } = getJWTData() || { orgid: null, patientid: null };
  if (!orgid || !patientid) return null;
  return fetchWithOptions(
    `/api/patient/v1/informants/unlink/${informantId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'DELETE' },
  )
    .then((res: Response) => {
      if (res.status === 200) return true;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(
        Loggable.BugActionable,
        e,
        new ActionableErrorContext(null, `DELETE unlinking patient "${patientid}" to informant "${informantId}"`),
      );
      return false;
    });
};

/**
 * Fetch informant by ssn and ssnLocalization
 */
export const fetchInformantBySsn = async (ssn: string, ssnLocalization: string): Promise<IInformant | null> => {
  return fetchWithOptions(
    `/api/patient/v1/informants/${ssn}?ssn_locale=${ssnLocalization}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(res => {
      if (res.status === 200) return res.json() as unknown as IInformant;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET informant by ssn'));
      return null;
    });
};
